import { useEffect, useMemo, useState } from 'react'
import type { Media, SiteSettings } from '@nhanho/shared'
import { asMedia } from '@nhanho/shared'
import { settings as api, explainError } from '../../lib/api'
import { useToast } from '../../lib/toast'
import { useDirtyGuard } from '../../lib/dirty'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Spinner } from '../../components/ui/Spinner'
import { MediaPicker } from '../../components/media/MediaPicker'
import { SortableGrid } from '../../components/media/SortableImages'
import { SquareCropper, defaultCrop, type CropValue } from '../../components/media/SquareCropper'

/**
 * "Trang chủ" — nội dung do admin đặt cho trang chủ của web (2026-09-23).
 *
 * Hiện chỉ có dải "Cảm hứng từ Nhà Nhỏ". Tách thành trang riêng chứ không nhét vào
 * `/settings`: trang đó tên là **Cài đặt SEO** và đã dài 453 dòng — thêm một khối nội dung vào
 * đó là bắt người dùng đi tìm ảnh trang chủ trong mục SEO.
 *
 * ⚠️ Lưu bằng chính `PATCH /api/settings` (dải ảnh nằm trong singleton `settings`). Nghĩa là
 * **hai trang cùng ghi một document**: mở cả hai tab rồi lưu lần lượt thì tab lưu sau không ghi
 * đè tab trước — body chỉ chứa field của trang đó, `findOneAndUpdate` trộn chứ không thay cả
 * document.
 */

/** Một ô trong dải, ở dạng form đang sửa. `media` giữ nguyên object để còn hiện thumbnail. */
interface Slot {
  /** Khoá cho kéo–thả. Không dùng `media.id`: một ảnh được phép xuất hiện hai lần với hai khung. */
  key: string
  media: Media
  crop: CropValue
  /** URL ảnh vuông API đã cắt. Ô vừa thêm thì chưa có — xem trước bằng ảnh gốc + CSS. */
  url: string | null
}

let seq = 0
const newKey = () => `slot-${Date.now()}-${seq++}`

function fromDoc(s: SiteSettings): Slot[] {
  return (s.homeInspiration ?? []).flatMap((it) => {
    const media = asMedia(it.media)
    /**
     * `asMedia` trả `null` khi ảnh gốc đã bị xoá khỏi thư viện. Bỏ ô đó đi thay vì dựng một ô
     * rỗng: ô rỗng thì không mở cắt được, không biết là ảnh nào, và sẽ được gửi lại lên API
     * với một `media` không tồn tại → 422 cho cả lần lưu.
     */
    if (!media) return []
    return [{ key: newKey(), media, crop: it.crop as CropValue, url: it.url }]
  })
}

export default function HomeContent() {
  const toast = useToast()
  const [slots, setSlots] = useState<Slot[] | null>(null)
  const [initial, setInitial] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [picking, setPicking] = useState(false)
  const [editing, setEditing] = useState<Slot | null>(null)

  useEffect(() => {
    api.get()
      .then((s) => { const next = fromDoc(s); setSlots(next); setInitial(serialize(next)) })
      .catch((e) => toast.error(explainError(e).message))
  }, [toast])

  /** So sánh trên **body sẽ gửi đi**, không trên cả `Slot`: `media` là object lớn và có `updatedAt`. */
  const dirty = useMemo(() => !!slots && serialize(slots) !== initial, [slots, initial])
  useDirtyGuard(dirty)

  if (!slots) return <Spinner />

  const save = async () => {
    setBusy(true)
    try {
      const saved = await api.update({ homeInspiration: toBody(slots) })
      const next = fromDoc(saved)
      setSlots(next); setInitial(serialize(next))
      toast.success(next.length ? 'Đã lưu dải ảnh trang chủ' : 'Đã xoá hết — trang chủ quay về 11 ảnh mặc định')
    } catch (e) {
      toast.error(explainError(e).message)
    } finally { setBusy(false) }
  }

  const addPicked = (docs: Media[]) => {
    setSlots((xs) => [
      ...(xs ?? []),
      ...docs.map((m) => ({ key: newKey(), media: m, crop: defaultCrop(m.width, m.height), url: null })),
    ])
  }

  return (
    <>
      <PageHeader
        title="Trang chủ"
        subtitle="Nội dung hiển thị trên trang chủ của web"
        actions={<Button onClick={save} loading={busy} disabled={!dirty && !busy}>{dirty ? 'Lưu' : 'Đã lưu'}</Button>}
      />

      <Card title='Dải ảnh · "Cảm hứng từ Nhà Nhỏ"'>
        <p className="mb-4 text-xs text-ink-muted">
          Dải ảnh vuông cuộn ngang ở cuối trang chủ, tự chạy 3 giây một ảnh. Kéo để đổi thứ tự —
          ảnh đầu tiên là ảnh khách thấy trước. Ảnh nào cũng được cắt về ô vuông; bấm{' '}
          <strong>Sửa khung</strong> để chọn phần muốn giữ.
        </p>

        {slots.length === 0 ? (
          <div className="rounded-card-sm border border-dashed border-border-strong p-6 text-center text-sm text-ink-muted">
            Chưa chọn ảnh nào — trang chủ đang dùng <strong>11 ảnh mặc định</strong> có sẵn trong mã nguồn.
          </div>
        ) : (
          <SortableGrid items={slots} idOf={(s) => s.key} onChange={setSlots}>
            {(slot) => (
              <>
                {/*
                  Ô đã lưu thì hiện **ảnh vuông thật API đã cắt**; ô vừa thêm/vừa sửa khung thì
                  chưa có file nào, nên xem trước bằng ảnh gốc + `object-position` xấp xỉ.
                  ⚠️ Xấp xỉ, không phải xem trước chính xác: `object-fit: cover` không diễn tả
                  được mức phóng. Đó là lý do phải lưu mới thấy đúng — và cũng là lý do ô chưa
                  lưu có nhãn riêng bên dưới.
                */}
                <img
                  src={slot.url ?? slot.media.url}
                  alt={slot.media.alt}
                  className="pointer-events-none absolute inset-0 size-full object-cover"
                />
                {!slot.url && (
                  <span className="pointer-events-none absolute inset-x-0 top-0 bg-primary-700/85 px-1.5 py-0.5 text-2xs text-white">
                    Chưa lưu — xem trước
                  </span>
                )}
                <div className="absolute inset-x-1 bottom-1 hidden justify-between gap-1 group-hover:flex">
                  <Button size="sm" variant="secondary" className="h-6 px-2 text-2xs" onClick={() => setEditing(slot)}>
                    Sửa khung
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-6 px-2 text-2xs text-danger"
                    aria-label={`Bỏ ảnh: ${slot.media.alt}`}
                    onClick={() => setSlots((xs) => (xs ?? []).filter((x) => x.key !== slot.key))}
                  >
                    Bỏ
                  </Button>
                </div>
              </>
            )}
          </SortableGrid>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button variant="secondary" onClick={() => setPicking(true)}>Thêm ảnh</Button>
          <span className="text-xs text-ink-muted">{slots.length}/24 ảnh</span>
        </div>
      </Card>

      <MediaPicker open={picking} onClose={() => setPicking(false)} onPick={addPicked} multiple />

      <SquareCropper
        open={!!editing}
        media={editing?.media ?? null}
        value={editing?.crop ?? null}
        onClose={() => setEditing(null)}
        onSave={(crop) => {
          setSlots((xs) => (xs ?? []).map((x) => (x.key === editing?.key ? { ...x, crop, url: null } : x)))
          setEditing(null)
        }}
      />
    </>
  )
}

/** Body gửi lên API — **chỉ `media` + `crop`**; `url`/`width`/`height` do server cắt ra. */
function toBody(slots: Slot[]) {
  return slots.map((s) => ({ media: s.media.id, crop: s.crop }))
}

const serialize = (slots: Slot[]) => JSON.stringify(toBody(slots))
